import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { taskAutomationAPI, shortDate } from '../../lib/taskTemplatesAPI';

const minus = (iso, days) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - days); return d.toISOString().slice(0, 10);
};

// "Suggested deadline" + Statutory / Internal due dates. value: {deadline_rule_id, statutory_due_date, internal_due_date, overridden, confirmed}
export const DeadlineFields = ({ templateId, periodKey, value, onChange }) => {
  const [sug, setSug] = useState(null);
  useEffect(() => {
    if (!templateId) { setSug(null); return; }
    taskAutomationAPI.suggest(templateId, periodKey).then((r) => {
      setSug(r.data);
      const rule = r.data.rules.find((x) => x.id === r.data.default_rule_id);
      onChange({ deadline_rule_id: rule?.id || null, statutory_due_date: rule?.statutory_due_date || '', overridden: false,
        internal_due_date: minus(rule?.statutory_due_date, r.data.internal_offset_days), confirmed: rule?.status === 'verified' });
    }).catch(() => setSug(null));
  }, [templateId, periodKey]); // eslint-disable-line
  const rule = sug?.rules.find((x) => x.id === value.deadline_rule_id);
  const pick = (id) => {
    const r = sug.rules.find((x) => x.id === id);
    onChange({ ...value, deadline_rule_id: id, statutory_due_date: r.statutory_due_date, overridden: false,
      internal_due_date: minus(r.statutory_due_date, sug.internal_offset_days), confirmed: r.status === 'verified' });
  };
  const setStat = (d) => {
    const internal = !value.internal_due_date || value.internal_due_date > d ? minus(d, sug?.internal_offset_days ?? 7) : value.internal_due_date;
    onChange({ ...value, statutory_due_date: d, internal_due_date: d ? internal : value.internal_due_date, overridden: true });
  };
  const setInternal = (d) => onChange({ ...value, internal_due_date: value.statutory_due_date && d > value.statutory_due_date ? value.statutory_due_date : d, overridden: true });
  return (
    <div className="space-y-2 rounded-lg border p-3 bg-muted/20" data-testid="task-deadline-fields">
      {sug && sug.rules.length > 0 && (
        <div className="space-y-1.5">
          <Label>Suggested deadline</Label>
          <Select value={value.deadline_rule_id || ''} onValueChange={pick}>
            <SelectTrigger data-testid="task-deadline-rule-select"><SelectValue placeholder="Choose a lodgement rule" /></SelectTrigger>
            <SelectContent>{sug.rules.map((r) => (
              <SelectItem key={r.id} value={r.id} data-testid={`deadline-option-${r.id}`}>{r.label} — {shortDate(r.statutory_due_date)}{r.status !== 'verified' ? ' (needs verification)' : ''}</SelectItem>))}</SelectContent>
          </Select>
          {rule && rule.status !== 'verified' && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline" className="border-amber-400 text-amber-700" data-testid="deadline-needs-verification"><AlertTriangle className="w-3 h-3 mr-1" />Needs admin verification</Badge>
              <label className="flex items-center gap-1.5"><Checkbox checked={!!value.confirmed} onCheckedChange={(v) => onChange({ ...value, confirmed: !!v })} data-testid="deadline-confirm-checkbox" />I've checked this date</label>
            </div>
          )}
        </div>
      )}
      {sug && sug.no_rule && <p className="text-xs text-muted-foreground" data-testid="deadline-no-rule">No rule – enter manually{sug.manual_rules[0] ? ` (${sug.manual_rules[0].label})` : ''}</p>}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label htmlFor="statutory_due">Statutory / ATO due date</Label>
          <Input id="statutory_due" type="date" value={value.statutory_due_date || ''} onChange={(e) => setStat(e.target.value)} data-testid="task-statutory-due-input" /></div>
        <div className="space-y-1"><Label htmlFor="internal_due">Internal due date</Label>
          <Input id="internal_due" type="date" value={value.internal_due_date || ''} onChange={(e) => setInternal(e.target.value)} data-testid="task-due-input" /></div>
      </div>
      {sug && value.statutory_due_date && <p className="text-[11px] text-muted-foreground">Internal = statutory minus {sug.internal_offset_days} days (never later than statutory).</p>}
    </div>
  );
};
